const isLocalDevelopment = process.env.NODE_ENV === 'development';

/** Backend local implicit (aliniat cu backend/src/config/app.ts port 3000). */
const DEFAULT_LOCAL_BACKEND_API_BASE = 'http://localhost:3000/api/v1';

/** Portul pe care rulează UI-ul în package.json (craco start); nu servește /api/v1. */
const LOCAL_FRONTEND_STATIC_PORT = '3005';

const normalizeBaseUrl = (value: string): string => value.replace(/\/+$/, '');

const isLocalhostHostname = (host: string): boolean =>
  host === 'localhost' || host === '127.0.0.1';

/**
 * Dacă utilizatorul a setat accidental API-ul pe același port ca frontend-ul (3005),
 * folosim backend-ul pe 3000 — altfel login-ul lovește CRA/serve și primește 404.
 */
const shouldRewriteMisconfiguredApiUrl = (normalizedUrl: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const parsed = new URL(normalizedUrl);
    const port = parsed.port || (parsed.protocol === 'https:' ? '443' : '80');
    return isLocalhostHostname(parsed.hostname) && port === LOCAL_FRONTEND_STATIC_PORT;
  } catch {
    return false;
  }
};

const allowRemoteApiOnLocalhost = process.env.REACT_APP_ALLOW_REMOTE_API_ON_LOCALHOST === 'true';

const resolveApiBaseUrl = (): string => {
  const explicitBaseUrl =
    process.env.REACT_APP_API_BASE_URL?.trim() || process.env.REACT_APP_API_URL?.trim();

  if (typeof window !== 'undefined' && !allowRemoteApiOnLocalhost) {
    const host = window.location.hostname;
    const port = window.location.port || (window.location.protocol === 'https:' ? '443' : '80');
    const isLocalUi = isLocalhostHostname(host) && (port === LOCAL_FRONTEND_STATIC_PORT || port === '3000');
    if (isLocalUi) {
      return normalizeBaseUrl(DEFAULT_LOCAL_BACKEND_API_BASE);
    }
  }

  if (explicitBaseUrl) {
    const normalized = normalizeBaseUrl(explicitBaseUrl);
    if (shouldRewriteMisconfiguredApiUrl(normalized)) {
      return normalizeBaseUrl(DEFAULT_LOCAL_BACKEND_API_BASE);
    }
    return normalized;
  }

  if (!isLocalDevelopment && typeof window !== 'undefined') {
    const { origin, port, hostname } = window.location;
    const effectivePort = port || (window.location.protocol === 'https:' ? '443' : '80');
    const onLocalFrontendPreview =
      isLocalhostHostname(hostname) && effectivePort === LOCAL_FRONTEND_STATIC_PORT;

    // build de producție servit cu `serve -s build -l 3005` → same-origin /api/v1 nu există pe static server
    if (onLocalFrontendPreview) {
      return normalizeBaseUrl(DEFAULT_LOCAL_BACKEND_API_BASE);
    }

    return normalizeBaseUrl(`${origin}/api/v1`);
  }

  return normalizeBaseUrl(DEFAULT_LOCAL_BACKEND_API_BASE);
};

const getApiOrigin = (baseUrl: string): string => (
  baseUrl.replace(/\/api\/v\d+\/?$/, '').replace(/\/+$/, '')
);

const buildApiUrl = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
};

const API_BASE = resolveApiBaseUrl();
const resolvedApiOrigin = getApiOrigin(API_BASE);

const API_CONFIG = {
  BASE_URL: API_BASE,
  ORIGIN: resolvedApiOrigin,
  TIMEOUT: 10000,
  IS_LOCAL: isLocalDevelopment,
  ENDPOINTS: {
    AUTH: {
      LOGIN: '/auth/login',
      REGISTER: '/auth/register',
      LOGOUT: '/auth/logout',
      REFRESH: '/auth/refresh',
      STATUS: '/auth/status'
    },
    USERS: {
      PROFILE: '/users/profile',
      UPDATE: '/users/profile',
      DELETE: '/users/profile'
    },
    CLINICS: {
      LIST: '/clinics',
      DETAILS: '/clinics/:id',
      CREATE: '/clinics',
      UPDATE: '/clinics/:id',
      DELETE: '/clinics/:id'
    },
    APPOINTMENTS: {
      LIST: '/appointments',
      DETAILS: '/appointments/:id',
      CREATE: '/appointments',
      UPDATE: '/appointments/:id',
      DELETE: '/appointments/:id',
      STATS: '/appointments/stats',
      CONFIRM: '/appointments/:id/confirm',
      CANCEL: '/appointments/:id/cancel',
      COMPLETE: '/appointments/:id/complete',
      BY_DATE: '/appointments/date/:date'
    },
    ANIMALS: {
      LIST: '/animals',
      DETAILS: '/animals/:id',
      CREATE: '/animals',
      UPDATE: '/animals/:id',
      DELETE: '/animals/:id'
    }
  }
};

if (isLocalDevelopment && typeof window !== 'undefined') {
  console.info('[api] Base URL:', API_CONFIG.BASE_URL);
}

export const API_BASE_URL = API_CONFIG.BASE_URL;
export const API_ORIGIN = API_CONFIG.ORIGIN;
export const IS_LOCAL_DEVELOPMENT = API_CONFIG.IS_LOCAL;
export { buildApiUrl };
export default API_CONFIG;
